import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { REGISTER_URL } from "@/data/site";
import SignupWizard from "@/pages/SignupWizard";

/* SignupModalProvider · the in-app signup-wizard popup.
 *
 * SCOPED · only the header "Open Account" button triggers this modal,
 * and only when the admin has flipped `signup_wizard_enabled` on in
 * the Admin → Settings dashboard. Every OTHER "Open Account" CTA on
 * the site keeps its original behaviour (links straight to the CRM's
 * hosted signup page in a new tab). This keeps the popup an
 * opt-in upgrade so ops can disable it instantly during a CRM
 * outage / captcha maintenance window.
 *
 * The provider:
 *   1) Renders <SignupWizard compact /> inside a shadcn Dialog
 *      (with a visually-hidden DialogTitle for screen-reader a11y).
 *   2) Exposes useSignupModal() · { open, close, isOpen }.
 *   3) Listens for clicks on `<a href="#open-signup">` site-wide ·
 *      only the Navbar emits this href, and only when the admin has
 *      enabled the wizard.
 *   4) Reacts to direct navigation to `/signup`, `/register`,
 *      `/open-account` · if the admin has enabled the wizard, it
 *      auto-opens the modal and rewrites the URL back to "/" so
 *      partner share links (`/signup?ref=ib-77`) still work. If
 *      the wizard is disabled, the user gets sent straight to the
 *      external CRM signup page instead.
 */

const SignupModalContext = createContext({
  isOpen: false,
  open:  () => {},
  close: () => {},
});

export function useSignupModal() {
  return useContext(SignupModalContext);
}

export default function SignupModalProvider({ children }) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  // The wizard publishes its current step (1..5) up to the provider
  // via the `onStepChange` callback so the modal can paint the top
  // progress bar without coupling the bar's render path to the
  // wizard's internal step state.
  const [step, setStep] = useState(1);
  const location = useLocation();
  const nav = useNavigate();
  const { signupWizardEnabled, loaded: settingsLoaded } = useSiteSettings();

  const open  = useCallback(() => setOpen(true),  []);
  const close = useCallback(() => setOpen(false), []);

  /* ① Global click interceptor for <a href="#open-signup"> ─────── */
  // Only the navbar emits this href (and only when the admin has
  // enabled the wizard), so in practice this handler only fires from
  // header clicks. The `enabled` check below is defensive · if a
  // future build accidentally emits the sentinel from somewhere else,
  // or the admin toggles the wizard off mid-session, the click
  // falls through to the external CRM signup link instead.
  useEffect(() => {
    const handler = (e) => {
      // Allow new-tab / new-window / right-click intents to fall through.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href="#open-signup"]');
      if (!a) return;
      e.preventDefault();
      if (!signupWizardEnabled) {
        window.open(REGISTER_URL, "_blank", "noopener,noreferrer");
        return;
      }
      setOpen(true);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [signupWizardEnabled]);

  /* ②a Hash-landing support · `/<any-path>#open-signup` auto-opens ──── */
  // When a visitor lands on (or in-app navigates to) a URL whose hash
  // is `#open-signup` we pop the modal and strip the hash so the
  // address bar stays clean. Mirrors the click-handler semantics:
  // if the wizard is disabled we send them to the external CRM
  // signup page instead, so share links like
  // `https://ofinancial.markets/#open-signup` always lead to a
  // signup flow regardless of which mode the broker is in.
  useEffect(() => {
    if (location.hash !== "#open-signup") return;
    // Wait for /api/site-settings to resolve so we don't race-redirect
    // visitors to the external CRM before the admin's wizard-enabled
    // flag has hydrated.
    if (!settingsLoaded) return;
    if (signupWizardEnabled) {
      queueMicrotask(() => setOpen(true));
      // Drop the hash but keep the current path + query.
      nav({ pathname: location.pathname, search: location.search, hash: "" }, { replace: true });
    } else {
      window.location.replace(REGISTER_URL);
    }
  }, [location.hash, location.pathname, location.search, nav, signupWizardEnabled, settingsLoaded]);

  /* ② Deep-link support · /signup, /register, /open-account auto-open ── */
  // If the admin has the wizard enabled we pop the modal and rewrite
  // the URL back to "/" so the user isn't stranded on a hash-only
  // path. If the wizard is disabled we instead hard-redirect to the
  // external CRM signup URL · this preserves share-link semantics
  // ("come open an account") regardless of which mode the broker is
  // currently running in.
  //
  // CROSS-DEVICE PICKUP · when the URL carries a `?resume=TOKEN`
  // query string we ALWAYS open the modal (regardless of the admin
  // toggle) AND preserve the search string when rewriting the URL.
  // Otherwise (a) the race between this effect and the async
  // useSiteSettings() hydration would bounce the user to the
  // external CRM, and (b) stripping the query in the nav call
  // would prevent SignupWizard from reading the resume token on
  // mount — both of which break the share-link round-trip.
  useEffect(() => {
    const p = location.pathname;
    if (p !== "/signup" && p !== "/register" && p !== "/open-account") return;
    const params = new URLSearchParams(location.search || "");
    const hasResumeToken = !!params.get("resume");
    // Wait for /api/site-settings to resolve (unless a resume token
    // forces the modal open regardless of admin toggle) so we don't
    // race-redirect to the external CRM before hydration completes.
    if (!settingsLoaded && !hasResumeToken) return;
    if (signupWizardEnabled || hasResumeToken) {
      // Schedule the state update outside the effect's render commit so
      // the react-hooks/set-state-in-effect rule is satisfied. The
      // microtask still runs synchronously before the next paint so the
      // modal pops without a perceptible flash.
      queueMicrotask(() => setOpen(true));
      // Preserve the search string so SignupWizard's mount-time
      // useEffect can still see ?resume=TOKEN on window.location.search.
      nav({ pathname: "/", search: location.search }, { replace: true });
    } else {
      // Replace history entry so the back button doesn't bounce the
      // visitor between the marketing site and the CRM signup page.
      window.location.replace(REGISTER_URL);
    }
  }, [location.pathname, location.search, nav, signupWizardEnabled, settingsLoaded]);

  return (
    <SignupModalContext.Provider value={{ isOpen, open, close }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent
          data-testid="signup-modal"
          className="
            signup-wizard-skin
            w-[min(96vw,720px)] max-w-none p-0 overflow-hidden border-0
            max-h-[92dvh] sm:rounded-2xl
            top-[max(env(safe-area-inset-top),12px)] translate-y-0
            sm:top-[50%] sm:-translate-y-1/2
            bg-[color:var(--wiz-card)]
            [&>button[type='button']]:opacity-100
            [&>button[type='button']]:h-9
            [&>button[type='button']]:w-9
            [&>button[type='button']]:grid
            [&>button[type='button']]:place-items-center
            [&>button[type='button']]:rounded-full
            [&>button[type='button']]:bg-[color:color-mix(in_srgb,var(--wiz-text-1)_8%,transparent)]
            [&>button[type='button']]:border
            [&>button[type='button']]:border-[color:var(--wiz-line-strong)]
            [&>button[type='button']]:text-[color:var(--wiz-text-1)]
            [&>button[type='button']]:hover:bg-[color:color-mix(in_srgb,var(--wiz-text-1)_14%,transparent)]
            [&>button[type='button']]:transition-colors
            [&>button[type='button']]:right-4
            [&>button[type='button']]:top-5
            [&>button[type='button']]:z-20
            [&>button[type='button']_svg]:h-4
            [&>button[type='button']_svg]:w-4
            [&>button[type='button']_svg]:stroke-[2.5]
          "
          style={{ boxShadow: "var(--wiz-shadow)" }}
          onOpenAutoFocus={(e) => {
            // Prevent the auto-focus from snapping the page scroll on open.
            e.preventDefault();
          }}
        >
          {/* Top progress bar · fills as the user advances through
              the five steps. Pure decoration · the source of truth
              is the wizard's `step` state lifted up via onStepChange. */}
          <div className="wiz-progress-bar" aria-hidden="true">
            <span style={{ width: `${(step / 5) * 100}%` }} />
          </div>

          {/* Visually-hidden title · Radix's Dialog requires a
              DialogTitle for screen-reader users (otherwise it logs a
              big console error in dev). The wizard renders its own
              visible "Let's get you trading." headline inside so we
              hide this one off-screen. */}
          <VisuallyHidden.Root asChild>
            <DialogTitle>{t("comp.misc.signupModalTitle")}</DialogTitle>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root asChild>
            <DialogDescription>
              {t("comp.misc.signupModalDesc")}
            </DialogDescription>
          </VisuallyHidden.Root>

          {/* Aurora glow behind the headline · pure decorative */}
          <div className="wiz-aurora" aria-hidden="true" />

          {/* Inner scroll container · the wizard itself is tall and needs
              its own scroll surface inside the fixed-height dialog. */}
          <div className="relative z-10 overflow-y-auto px-5 pt-6 pb-6 sm:px-7 sm:pt-7 sm:pb-7"
               style={{ maxHeight: "calc(92dvh - 0px)" }}>
            {/* When the modal is open, mount a fresh wizard each time so
                opening twice in a row resets all the step state · key
                bound to `isOpen` keeps the mount cycle predictable. */}
            {isOpen && (
              <SignupWizard
                key={String(isOpen)}
                compact
                onComplete={close}
                onStepChange={setStep}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </SignupModalContext.Provider>
  );
}
